import React from "react";

import { type DefaultProps, Layer, WebMercatorViewport } from "@deck.gl/core";
import type { FeatureCollection } from "geojson";
import { isEqual, some } from "lodash-es";
import type {
  DataDrivenPropertyValueSpecification,
  FormattedSpecification,
  LayerSpecification,
  LngLatLike,
  Map as MapType,
  ResourceType,
  VectorSourceSpecification,
} from "maplibre-gl";

import { AppUI, type WindowDimensions } from "./../../app/appSlice";

import type { ConfigState } from "@/features/config/types";
import { clamp } from "@/libs/geo";
import { BoundingBox, type Building, LatLng } from "../../../libs/models";
import {
  DEFAULT_PADDING,
  INITIAL_BEARING,
  INITIAL_PITCH,
  MAX_BEARING,
  MAX_PITCH,
  MAX_ZOOM,
  MIN_BEARING,
  MIN_PITCH,
  MIN_ZOOM,
} from "../constants";
import type { ExtendedStyleSpecification } from "../hooks/maplibre/useMaplibreStyleSpec";
import type { SliceProps } from "../types";

export const getPaddedBoundingBox = ({
  bounds,
  windowSize,
  paddingRatio,
  paddingRatioVertical,
  paddingRatioHorizontal,
}: {
  bounds: LatLng[];
  windowSize: { height: number; width: number };
  paddingRatio?: number;
  paddingRatioVertical?: number;
  paddingRatioHorizontal?: number;
}): [LatLng, LatLng] => {
  // Fit bounds to window
  const windowBounds = getBoundsForWindow(windowSize, bounds);

  const lngDiff = windowBounds[2] - windowBounds[0];
  const latDiff = windowBounds[3] - windowBounds[1];

  const effectivePaddingRatioVertical =
    paddingRatioVertical ?? paddingRatio ?? DEFAULT_PADDING;
  const effectivePaddingRatioHorizontal =
    paddingRatioHorizontal ?? paddingRatio ?? DEFAULT_PADDING;

  const lngPadding = (effectivePaddingRatioHorizontal * lngDiff) / 2;
  const latPadding = (effectivePaddingRatioVertical * latDiff) / 2;

  const sw = new LatLng(
    windowBounds[1] - latPadding,
    windowBounds[0] - lngPadding,
  );
  const ne = new LatLng(
    windowBounds[3] + latPadding,
    windowBounds[2] + lngPadding,
  );

  return [sw, ne];
};

export const getBoundsForWindow = (
  windowSize: { width: number; height: number },
  bounds: LatLng[],
): [number, number, number, number] => {
  const viewport = new WebMercatorViewport({
    ...windowSize,
  });

  const boundingBox = new BoundingBox(bounds);
  const sw = boundingBox.getSW();
  const ne = boundingBox.getNE();

  return viewport
    .fitBounds([
      [sw.lng, sw.lat],
      [ne.lng, ne.lat],
    ])
    .getBounds();
};

export const viewToFitBounds = ({
  windowSize,
  bounds,
  resizeFactor,
}: {
  windowSize: { width: number; height: number };
  bounds: LatLng[];
  resizeFactor?: { widthFactor: number; heightFactor: number };
}): { lat: number; lng: number; zoom: number } => {
  const adjustedWindowSize = resizeFactor
    ? {
        height: windowSize.height * clamp(resizeFactor.heightFactor, 0, 1),
        width: windowSize.width * clamp(resizeFactor.widthFactor, 0, 1),
      }
    : windowSize;

  const viewport = new WebMercatorViewport({
    ...adjustedWindowSize,
  });

  const boundingBox = new BoundingBox(bounds);
  const sw = boundingBox.getSW();
  const ne = boundingBox.getNE();

  const {
    latitude: lat,
    longitude: lng,
    zoom,
  } = viewport.fitBounds([
    [sw.lng, sw.lat],
    [ne.lng, ne.lat],
  ]);

  return { lat, lng, zoom: zoom };
};

export const computeBuildingViewLimits = (
  b: Building,
  windowSize: { width: number; height: number },
  configMapInitialView: ConfigState["map"]["initialView"],
  configCamera: ConfigState["camera"],
): SliceProps["mapViewLimits"] => {
  let minZoom = configCamera.minZoom;
  let maxZoom = configCamera.maxZoom;
  let initialZoom = configMapInitialView.zoom;
  let minPitch = MIN_PITCH;
  let maxPitch = MAX_PITCH;
  let initialPitch =
    configMapInitialView.pitch !== null
      ? configMapInitialView.pitch
      : INITIAL_PITCH;
  let minBearing = MIN_BEARING;
  let maxBearing = MAX_BEARING;
  let initialBearing = configMapInitialView.bearing || INITIAL_BEARING;

  //Set zoom parameters automatically if no zoom parameters were provided
  //TODO: Move to aux function. Improve maxZoom automatic calculation
  if (minZoom == null) {
    const { zoom } = viewToFitBounds({
      bounds: b.getBounds(),
      resizeFactor: {
        heightFactor: 0.7,
        widthFactor: 0.7,
      },
      windowSize,
    });
    if (initialZoom == null) minZoom = zoom;
    else minZoom = Math.min(initialZoom, zoom);
  }
  if (initialZoom == null) {
    const { zoom } = viewToFitBounds({ bounds: b.getBounds(), windowSize });
    initialZoom = zoom;
  }
  if (maxZoom == null) {
    const area = b.getArea();
    maxZoom =
      area < 600 ? MAX_ZOOM : area < 30000 ? MAX_ZOOM - 1 : MAX_ZOOM - 2;
  }

  //Clamp values betwen MIN_* and MAX_*
  maxZoom = clamp(maxZoom, MIN_ZOOM, MAX_ZOOM);
  minZoom = clamp(minZoom, MIN_ZOOM, maxZoom);
  initialZoom = clamp(initialZoom, minZoom, maxZoom);

  maxPitch = clamp(maxPitch, MIN_PITCH, MAX_PITCH);
  minPitch = clamp(minPitch, MIN_PITCH, maxPitch);
  initialPitch = clamp(initialPitch, minPitch, maxPitch);

  maxBearing = clamp(maxBearing, MIN_BEARING, MAX_BEARING);
  minBearing = clamp(minBearing, MIN_BEARING, maxBearing);
  initialBearing = clamp(initialBearing, minBearing, maxBearing);

  return {
    bboxPadding: DEFAULT_PADDING,
    initialBearing: initialBearing,
    initialLatitude: configMapInitialView.latitude,
    initialLongitude: configMapInitialView.longitude,
    initialPitch: initialPitch,
    initialZoom: initialZoom,
    maxBearing: maxBearing,
    maxPitch: maxPitch,
    maxZoom: maxZoom,
    minBearing: minBearing,
    minPitch: minPitch,
    minZoom: minZoom,
  };
};

export const createEmptyFeatureCollection = () => {
  return {
    features: [],
    type: "FeatureCollection",
  } as FeatureCollection;
};

export const inheritsFrom = <T>(Type: any, ParentType: T): Type is T => {
  while (Type) {
    if (Type === ParentType) {
      return true;
    }
    Type = Object.getPrototypeOf(Type);
  }
  return false;
};

export const isComponent = (
  child: React.ReactNode,
): child is React.ReactElement => {
  return (child && typeof child === "object" && "type" in child) || false;
};

const createLayer = <T extends Record<string, any>>(
  LayerType: typeof Layer,
  reactProps: T,
): Layer => {
  const props = {} as T;
  // Layer.defaultProps is treated as ReactElement.defaultProps and merged into react props
  // Remove them
  const defaultProps = (LayerType.defaultProps as DefaultProps<T>) || {};
  for (const key in reactProps) {
    //@ts-expect-error
    if (defaultProps[key] !== reactProps[key]) {
      props[key] = reactProps[key];
    }
  }

  //@ts-expect-error
  return new LayerType(props);
};

export const createDeckGLLayers = ({
  children,
  layers = [],
}: {
  children?: React.ReactNode;
  layers?: Layer[];
}) => {
  const layersFromJsx = React.Children.map(children, (child) => {
    if (!React.isValidElement(child) || !isComponent(child)) return;
    const ElementType = child.type;
    if (inheritsFrom(ElementType, Layer)) {
      return createLayer<typeof child.props>(ElementType, child.props);
    }
  })?.filter(Boolean) as [];

  return layersFromJsx?.length > 0 ? [...layersFromJsx, ...layers] : layers;
};

/**
 * Calculates the padding to be used in the map.easeTo method.
 *
 * @returns padding object to be used in map.easeTo
 * @description This function calculates the padding to be used in the map.easeTo method.
 * The padding is calculated based on the app mode and the device type (tablet or mobile).
 * The padding is used to avoid the map going over the viewport limits.
 */
export const calculatePadding = ({
  isTabletOrHigher,
  appMode,
  kioskSidebarWidth,
  windowDimensions,
  isRtl,
}: {
  isTabletOrHigher: boolean;
  appMode: string;
  kioskSidebarWidth?: number;
  windowDimensions: WindowDimensions;
  isRtl: boolean;
}) => {
  let top = 0;
  let bottom = 0;
  if (isTabletOrHigher) {
    // if ([AppUI.explore].includes(appMode)) {
    //   top = windowDimensions.height / 4;
    // } else if (appMode === AppUI.navigation) {
    //   top = windowDimensions.height / 3;
    // }
  } else {
    // Mobile
    if (appMode === AppUI.navigation) {
      top = windowDimensions.height / 2;
    } else if ([AppUI.directions, AppUI.steps].includes(appMode)) {
      bottom = windowDimensions.height / 10;
    }
  }

  let horizontalPadding = 0;
  if (isTabletOrHigher && [AppUI.directions, AppUI.explore].includes(appMode)) {
    horizontalPadding = kioskSidebarWidth != null ? kioskSidebarWidth / 3 : 200;
  }
  return {
    bottom,
    left: !isRtl ? horizontalPadding : 0,
    right: isRtl ? horizontalPadding : 0,
    top,
  };
};

export const isPMTilesRequest = ({
  url,
  resourceType,
}: {
  url: string;
  resourceType?: ResourceType;
}) => {
  return (
    (resourceType === "Tile" || resourceType === "Source") &&
    /https?:\/\/[^/]*situm\.com\/api\/v1\/vectortiles/.test(url)
  );
};

export const getLatLng = (point: LngLatLike) => {
  if (Array.isArray(point)) {
    const [longitude, latitude] = point;
    return { latitude: latitude, longitude: longitude };
  }

  if ("lat" in point && "lng" in point) {
    return { latitude: point.lat, longitude: point.lng };
  }

  if ("lat" in point && "lon" in point) {
    return { latitude: point.lat, longitude: point.lon };
  }

  throw new Error("Invalid point type");
};
